import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import SearchBar from '../Search/SearchBar';
import TransactionDetails from '../Details/TransactionDetails';
import BlockDetails from '../Details/BlockDetails';
import { NetworkMetrics } from '../Performance';

const Dashboard = ({ chainData, consensusMetrics, performanceMetrics, supply, networkStats }) => {
  const [searchResult, setSearchResult] = useState(null);
  const [showTokenSection, setShowTokenSection] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [recentBlocks, setRecentBlocks] = useState([]);
  const [selectedTransactionHash, setSelectedTransactionHash] = useState(null);
  const [selectedBlockHash, setSelectedBlockHash] = useState(null);
  const [showAllBlocks, setShowAllBlocks] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [blocksPage, setBlocksPage] = useState(1);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const itemsPerPage = 6;

  // Helper function to safely format timestamps
  const formatTimestamp = (timestamp, formatType = 'time') => {
    if (!timestamp || timestamp === 0) {
      return 'N/A';
    }
    
    try {
      // Handle microsecond timestamps (> 1 trillion)
      // Unix timestamps in seconds are ~1.7 billion (year 2024)
      // Microsecond timestamps are ~1.7 trillion
      let timestampInSeconds = timestamp;
      if (timestamp > 10000000000) {  // If > 10 billion, it's microseconds
        timestampInSeconds = Math.floor(timestamp / 1000000); // Convert microseconds to seconds
      }
      
      const date = new Date(timestampInSeconds * 1000); // Convert to milliseconds for JavaScript Date
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      if (formatType === 'time') {
        return date.toLocaleTimeString();
      } else if (formatType === 'full') {
        return date.toLocaleString();
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return 'N/A';
    }
  };

  // Helper functions for pagination
  const getDisplayedBlocks = () => {
    if (showAllBlocks) {
      const startIndex = (blocksPage - 1) * itemsPerPage;
      return recentBlocks.slice(startIndex, startIndex + itemsPerPage);
    }
    return recentBlocks.slice(0, itemsPerPage);
  };

  const getDisplayedTransactions = () => {
    if (showAllTransactions) {
      const startIndex = (transactionsPage - 1) * itemsPerPage;
      return recentTransactions.slice(startIndex, startIndex + itemsPerPage);
    }
    return recentTransactions.slice(0, itemsPerPage);
  };

  const getTotalBlocksPages = () => Math.ceil(recentBlocks.length / itemsPerPage);
  const getTotalTransactionsPages = () => Math.ceil(recentTransactions.length / itemsPerPage);

  // Helper function to format large numbers compactly
  const formatSupplyValue = (value) => {
    if (!value || value === 0) return '0.00';
    const num = parseFloat(value);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    }
    return num.toFixed(2);
  };

  // Function to generate page numbers for pagination
  const generatePageNumbers = (currentPage, totalPages) => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than or equal to max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // Adjust start if we're near the end
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add visible pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const chain = chainData.chain || [];
  
  // Use real total transactions from RPC instead of calculating from incomplete chain data
  const totalTxsFromRPC = networkStats?.total_transactions || 0;
  const totalTxsFromChain = chain.reduce((sum, block) => sum + (block.transactions?.length || 0), 0);
  
  // Calculate from recent blocks using transactionCount field
  const recentBlocksTxs = recentBlocks.reduce((sum, block) => sum + (block.transactionCount || 0), 0);
  
  // Use RPC value if available, otherwise fallback to calculated values
  const displayTotalTxs = totalTxsFromRPC > 0 ? totalTxsFromRPC : Math.max(totalTxsFromChain, recentBlocksTxs, recentTransactions.length);
  
  const calculateTPS = (chain) => {
    if (chain.length < 2) return 0;
    try {
      const last = chain[chain.length - 1];
      const first = chain[chain.length - Math.min(chain.length, 10)];
      const txCount = chain.slice(-10).reduce((sum, b) => sum + (b.transactions?.length || 0), 0);
      const timeDiff = (last.timestamp - first.timestamp) || 1;
      return (txCount / timeDiff).toFixed(4);
    } catch {
      return 0;
    }
  };

  const tps = calculateTPS(chain);

  // Function to fetch ALL transactions from AVO Protocol RPC
  const fetchRecentTransactions = async () => {
    try {
      const response = await fetch('http://localhost:9545', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'avo_getRecentTransactions',
          params: [10000], // Get up to 10,000 transactions (should cover all)
          id: 1
        })
      });
      
      const result = await response.json();
      if (result.result && result.result.transactions) {
        // Sort transactions by timestamp (newest first)
        const sortedTransactions = result.result.transactions.sort((a, b) => {
          const timeA = a.timestamp || a.block_timestamp || 0;
          const timeB = b.timestamp || b.block_timestamp || 0;
          return timeB - timeA; // Descendente: más recientes primero
        });
        setRecentTransactions(sortedTransactions);
      }
    } catch (error) {
      // Fallback to empty array if RPC fails
      setRecentTransactions([]);
    }
  };

  // Function to fetch ALL blocks from AVO Protocol RPC
  const fetchRecentBlocks = async () => {
    try {
      const response = await fetch('http://localhost:9545', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'avo_getRecentBlocks',
          params: [10000], // Get up to 10,000 blocks (should cover all)
          id: 2
        })
      });
      
      const result = await response.json();
      if (result.result && result.result.blocks) {
        // Sort blocks by height or timestamp (newest first)
        const sortedBlocks = result.result.blocks.sort((a, b) => {
          // First try to sort by block height/number
          if (a.height && b.height) {
            return b.height - a.height; // Descendente: bloque más alto primero
          }
          // Fallback to timestamp if no height
          const timeA = a.timestamp || a.block_timestamp || 0;
          const timeB = b.timestamp || b.block_timestamp || 0;
          return timeB - timeA; // Descendente: más recientes primero
        });
        setRecentBlocks(sortedBlocks);
      }
    } catch (error) {
      // Fallback to empty array if RPC fails
      setRecentBlocks([]);
    }
  };

  // Load real transactions and blocks on component mount and refresh every 10 seconds
  useEffect(() => {
    fetchRecentTransactions();
    fetchRecentBlocks();
    const interval = setInterval(() => {
      fetchRecentTransactions();
      fetchRecentBlocks();
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search functionality with real AVO Protocol RPC
  const handleSearch = async (query, searchData) => {
    if (!query.trim()) {
      setShowTokenSection(false);
      setSearchResult(null);
      return;
    }

    // Handle different search types
    if (searchData && searchData.type === 'address') {
      try {
        // Real wallet/address lookup using eth_getBalance
        const balanceResponse = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_getBalance',
            params: [query, 'latest'],
            id: 1
          })
        });
        
        const balanceResult = await balanceResponse.json();
        
        if (balanceResult.result) {
          // Convert hex balance to decimal AVO
          const balanceHex = balanceResult.result;
          const balanceWei = parseInt(balanceHex, 16);
          const balanceAVO = balanceWei / Math.pow(10, 18);
          
          // Check if it's a contract by trying to get code
          const codeResponse = await fetch('http://localhost:9545', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jsonrpc: '2.0',
              method: 'eth_getCode',
              params: [query, 'latest'],
              id: 2
            })
          });
          
          const codeResult = await codeResponse.json();
          const isContract = codeResult.result && codeResult.result !== '0x';
          
          const addressData = {
            address: query,
            balance: `${balanceAVO.toFixed(6)} AVO`,
            balanceWei: balanceHex,
            isContract: isContract,
            type: isContract ? 'Smart Contract' : 'External Account',
            name: isContract ? 'Smart Contract' : 'Wallet Address',
            symbol: isContract ? 'CONTRACT' : 'WALLET',
            network: 'AVO Protocol',
            verified: true,
            transactions: 'Real blockchain data',
            lastActivity: 'Live network'
          };
          
          setSearchResult(addressData);
          setShowTokenSection(true);
        } else {
          setSearchResult({
            address: query,
            error: 'Address not found or invalid',
            type: 'Error'
          });
          setShowTokenSection(true);
        }
      } catch (error) {
        console.error('Address lookup failed:', error);
        setSearchResult({
          address: query,
          error: `Search failed: ${error.message}`,
          type: 'Error'
        });
        setShowTokenSection(true);
      }
    } else if (searchData && searchData.type === 'block') {
      // Handle block search by number
      try {
        // Get all blocks and find the one with matching number
        const blocksResponse = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getRecentBlocks',
            params: [10000], // Get all blocks
            id: 1
          })
        });
        
        const blocksResult = await blocksResponse.json();
        if (blocksResult.result && blocksResult.result.blocks) {
          const foundBlock = blocksResult.result.blocks.find(
            block => (block.number === searchData.value || block.height === searchData.value)
          );

          if (foundBlock) {
            setSearchResult({
              type: 'Block',
              number: foundBlock.number || foundBlock.height,
              hash: foundBlock.hash,
              transactions: foundBlock.transactionCount || 0,
              gasUsed: foundBlock.gasUsed,
              gasLimit: foundBlock.gasLimit,
              timestamp: foundBlock.timestamp,
              timeFormatted: foundBlock.timeFormatted,
              validator: foundBlock.validator,
              size: foundBlock.size,
              merkleRoot: foundBlock.merkle_root,
              shardId: foundBlock.shardId
            });
            setShowTokenSection(true);
          } else {
            setSearchResult({
              error: `Block #${searchData.value} not found`,
              type: 'Error'
            });
            setShowTokenSection(true);
          }
        }
      } catch (error) {
        console.error('Block lookup failed:', error);
        setSearchResult({
          error: `Block search failed: ${error.message}`,
          type: 'Error'
        });
        setShowTokenSection(true);
      }
    } else if (searchData && (searchData.type === 'transaction' || searchData.type === 'block_hash')) {
      // Handle transaction hash or block hash search
      try {
        // First try to find it as a transaction
        const transactionsResponse = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getRecentTransactions',
            params: [10000], // Get all transactions
            id: 1
          })
        });

        const transactionsResult = await transactionsResponse.json();
        if (transactionsResult.result && transactionsResult.result.transactions) {
          const foundTransaction = transactionsResult.result.transactions.find(
            tx => tx.hash && tx.hash.toLowerCase() === searchData.value.toLowerCase()
          );

          if (foundTransaction) {
            setSearchResult({
              type: 'Transaction',
              hash: foundTransaction.hash,
              from: foundTransaction.from,
              to: foundTransaction.to,
              value: foundTransaction.valueInAVO ? `${foundTransaction.valueInAVO} AVO` : `${(foundTransaction.value / Math.pow(10, 18)).toFixed(6)} AVO`,
              gasUsed: foundTransaction.gasUsed,
              gasFee: foundTransaction.gasFeeInAVO ? `${foundTransaction.gasFeeInAVO} AVO` : `${(foundTransaction.gas_fee / Math.pow(10, 18)).toFixed(8)} AVO`,
              blockNumber: foundTransaction.blockNumber || foundTransaction.block_number,
              timestamp: foundTransaction.timestamp,
              status: foundTransaction.status || 'success'
            });
            setShowTokenSection(true);
            return;
          }
        }

        // If not found as transaction, try as block hash
        const blocksResponse = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getRecentBlocks',
            params: [10000], // Get all blocks
            id: 2
          })
        });

        const blocksResult = await blocksResponse.json();
        if (blocksResult.result && blocksResult.result.blocks) {
          const foundBlock = blocksResult.result.blocks.find(
            block => block.hash && block.hash.toLowerCase() === searchData.value.toLowerCase()
          );

          if (foundBlock) {
            setSearchResult({
              type: 'Block',
              number: foundBlock.number || foundBlock.height,
              hash: foundBlock.hash,
              transactions: foundBlock.transactionCount || 0,
              gasUsed: foundBlock.gasUsed,
              gasLimit: foundBlock.gasLimit,
              timestamp: foundBlock.timestamp,
              timeFormatted: foundBlock.timeFormatted,
              validator: foundBlock.validator,
              size: foundBlock.size,
              merkleRoot: foundBlock.merkle_root,
              shardId: foundBlock.shardId
            });
            setShowTokenSection(true);
            return;
          }
        }

        // If not found in either
        setSearchResult({
          error: 'Hash not found in transactions or blocks',
          type: 'Error',
          searchedHash: searchData.value
        });
        setShowTokenSection(true);

      } catch (error) {
        console.error('Hash lookup failed:', error);
        setSearchResult({
          error: `Hash search failed: ${error.message}`,
          type: 'Error'
        });
        setShowTokenSection(true);
      }
    } else if (searchData && searchData.type === 'partial_hash') {
      // Handle partial hash search
      try {
        // Search in transactions
        const transactionsResponse = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getRecentTransactions',
            params: [10000],
            id: 1
          })
        });

        const transactionsResult = await transactionsResponse.json();
        const matchingTransactions = [];
        const matchingBlocks = [];

        if (transactionsResult.result && transactionsResult.result.transactions) {
          transactionsResult.result.transactions.forEach(tx => {
            if (tx.hash && tx.hash.toLowerCase().includes(searchData.value.toLowerCase())) {
              matchingTransactions.push(tx);
            }
          });
        }

        // Search in blocks
        const blocksResponse = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getRecentBlocks',
            params: [10000],
            id: 2
          })
        });

        const blocksResult = await blocksResponse.json();
        if (blocksResult.result && blocksResult.result.blocks) {
          blocksResult.result.blocks.forEach(block => {
            if (block.hash && block.hash.toLowerCase().includes(searchData.value.toLowerCase())) {
              matchingBlocks.push(block);
            }
          });
        }

        setSearchResult({
          type: 'Partial Hash Search',
          searchTerm: searchData.value,
          matchingTransactions: matchingTransactions.slice(0, 5), // Limit to 5 results
          matchingBlocks: matchingBlocks.slice(0, 5),
          totalMatches: matchingTransactions.length + matchingBlocks.length
        });
        setShowTokenSection(true);

      } catch (error) {
        console.error('Partial hash lookup failed:', error);
        setSearchResult({
          error: `Partial hash search failed: ${error.message}`,
          type: 'Error'
        });
        setShowTokenSection(true);
      }
    } else {
      // Other search types or unknown
      setShowTokenSection(false);
      setSearchResult(null);
    }
  };

  return (
    <div className="dashboard-minimal">
      {/* Header with title and search */}
      <div className="dashboard-header-minimal">
        <h1 className="dashboard-title-minimal">AVO Protocol Network Overview</h1>
        <div className="search-container">
          <SearchBar onSearch={handleSearch} placeholder="Search tokens, contracts, blocks, transactions..." />
        </div>
      </div>

      {/* Search Results Section - Appears when searching */}
      {showTokenSection && searchResult && (
        <div className="search-results-section">
          <div className="section-header">
            <h2 className="section-title-minimal">
              {searchResult.type === 'Error' ? 'Search Error' : 
               searchResult.type === 'Block' ? 'Block Information' :
               searchResult.type === 'Transaction' ? 'Transaction Details' :
               searchResult.type === 'Partial Hash Search' ? 'Search Results' :
               searchResult.isContract ? 'Smart Contract' : 
               'Address Information'}
            </h2>
          </div>
          
          {searchResult.type === 'Error' ? (
            <div className="error-details">
              <div className="error-message">{searchResult.error}</div>
              {searchResult.address && (
                <div className="error-address">Address: {searchResult.address}</div>
              )}
              {searchResult.searchedHash && (
                <div className="error-address">Hash: {searchResult.searchedHash}</div>
              )}
            </div>
          ) : searchResult.type === 'Transaction' ? (
            <div className="transaction-details">
              <div className="transaction-info-grid">
                <div className="info-item">
                  <span className="label">Transaction Hash:</span>
                  <span className="value hash-value">{searchResult.hash}</span>
                </div>
                <div className="info-item">
                  <span className="label">From:</span>
                  <span className="value address-value">{searchResult.from}</span>
                </div>
                <div className="info-item">
                  <span className="label">To:</span>
                  <span className="value address-value">{searchResult.to}</span>
                </div>
                <div className="info-item">
                  <span className="label">Value:</span>
                  <span className="value amount-value">{searchResult.value}</span>
                </div>
                <div className="info-item">
                  <span className="label">Gas Used:</span>
                  <span className="value">{searchResult.gasUsed?.toLocaleString()}</span>
                </div>
                <div className="info-item">
                  <span className="label">Gas Fee:</span>
                  <span className="value">{searchResult.gasFee}</span>
                </div>
                <div className="info-item">
                  <span className="label">Block Number:</span>
                  <span className="value">{searchResult.blockNumber}</span>
                </div>
                <div className="info-item">
                  <span className="label">Status:</span>
                  <span className={`value status-${searchResult.status}`}>{searchResult.status}</span>
                </div>
                <div className="info-item">
                  <span className="label">Timestamp:</span>
                  <span className="value">{formatTimestamp(searchResult.timestamp, 'full')}</span>
                </div>
              </div>
            </div>
          ) : searchResult.type === 'Partial Hash Search' ? (
            <div className="partial-search-results">
              <div className="search-summary">
                <div className="info-item">
                  <span className="label">Search Term:</span>
                  <span className="value">{searchResult.searchTerm}</span>
                </div>
                <div className="info-item">
                  <span className="label">Total Matches:</span>
                  <span className="value">{searchResult.totalMatches}</span>
                </div>
              </div>
              
              {searchResult.matchingTransactions && searchResult.matchingTransactions.length > 0 && (
                <div className="matching-section">
                  <h4>Matching Transactions ({searchResult.matchingTransactions.length})</h4>
                  {searchResult.matchingTransactions.map((tx, index) => (
                    <div key={index} className="match-item">
                      <div className="match-hash" onClick={() => handleSearch(tx.hash, {type: 'transaction', value: tx.hash})} style={{cursor: 'pointer', color: '#00aaff'}}>
                        {tx.hash}
                      </div>
                      <div className="match-details">
                        From: {tx.from} → To: {tx.to} | Value: {(tx.value / Math.pow(10, 18)).toFixed(6)} AVO
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {searchResult.matchingBlocks && searchResult.matchingBlocks.length > 0 && (
                <div className="matching-section">
                  <h4>Matching Blocks ({searchResult.matchingBlocks.length})</h4>
                  {searchResult.matchingBlocks.map((block, index) => (
                    <div key={index} className="match-item">
                      <div className="match-hash" onClick={() => handleSearch(block.hash, {type: 'block_hash', value: block.hash})} style={{cursor: 'pointer', color: '#00aaff'}}>
                        {block.hash}
                      </div>
                      <div className="match-details">
                        Block #{block.number || block.height} | Transactions: {block.transactionCount} | Gas Used: {block.gasUsed?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : searchResult.type === 'Block' ? (
            <div className="block-details">
              <div className="block-info-grid">
                <div className="info-item">
                  <span className="label">Block Number:</span>
                  <span className="value">{searchResult.number}</span>
                </div>
                <div className="info-item">
                  <span className="label">Block Hash:</span>
                  <span className="value hash-value">{searchResult.hash}</span>
                </div>
                <div className="info-item">
                  <span className="label">Transactions:</span>
                  <span className="value">{searchResult.transactions}</span>
                </div>
                <div className="info-item">
                  <span className="label">Gas Used / Limit:</span>
                  <span className="value">{searchResult.gasUsed?.toLocaleString()} / {searchResult.gasLimit?.toLocaleString()}</span>
                </div>
                <div className="info-item">
                  <span className="label">Timestamp:</span>
                  <span className="value">{searchResult.timeFormatted || formatTimestamp(searchResult.timestamp, 'full')}</span>
                </div>
                <div className="info-item">
                  <span className="label">Validator:</span>
                  <span className="value">{searchResult.validator}</span>
                </div>
                <div className="info-item">
                  <span className="label">Block Size:</span>
                  <span className="value">{searchResult.size?.toLocaleString()} bytes</span>
                </div>
                <div className="info-item">
                  <span className="label">Shard ID:</span>
                  <span className="value">{searchResult.shardId}</span>
                </div>
              </div>
              
              {searchResult.transactions > 0 && (
                <div className="block-transactions-section">
                  <h4>Block Transactions</h4>
                  <div className="block-transaction-info">
                    <p>This block contains {searchResult.transactions} transactions.</p>
                    <button 
                      className="view-block-transactions-btn"
                      onClick={() => setSelectedBlockHash(searchResult.hash)}
                      style={{
                        backgroundColor: '#000000',
                        color: '#00aaff',
                        border: '1px solid #00aaff',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginTop: '10px'
                      }}
                    >
                      View All Transactions in Block
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="address-details">
              <div className="address-info-grid">
                <div className="info-item">
                  <span className="label">Address:</span>
                  <span className="value address-hash">{searchResult.address}</span>
                </div>
                <div className="info-item">
                  <span className="label">Type:</span>
                  <span className="value">{searchResult.type}</span>
                </div>
                <div className="info-item">
                  <span className="label">Balance:</span>
                  <span className="value balance-amount">{searchResult.balance}</span>
                </div>
                <div className="info-item">
                  <span className="label">Network:</span>
                  <span className="value">{searchResult.network}</span>
                </div>
                {searchResult.isContract && (
                  <>
                    <div className="info-item">
                      <span className="label">Contract Status:</span>
                      <span className="value contract-verified">Verified Smart Contract</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Contract Type:</span>
                      <span className="value">{searchResult.symbol}</span>
                    </div>
                  </>
                )}
                <div className="info-item">
                  <span className="label">Transaction History:</span>
                  <span className="value">{searchResult.transactions}</span>
                </div>
                <div className="info-item">
                  <span className="label">Last Activity:</span>
                  <span className="value">{searchResult.lastActivity}</span>
                </div>
              </div>
              
              {searchResult.balance && (
                <div className="balance-breakdown">
                  <h4>Balance Details</h4>
                  <div className="balance-item">
                    <span className="balance-label">Formatted Balance:</span>
                    <span className="balance-value">{searchResult.balance}</span>
                  </div>
                  <div className="balance-item">
                    <span className="balance-label">Wei (Raw):</span>
                    <span className="balance-value wei-value">{searchResult.balanceWei}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Network Metrics */}
      <div className="metrics-grid-minimal">
        <div className="metric-card-minimal">
          <div className="metric-content-minimal">
            <div className="metric-label-minimal">Current Epoch</div>
            <div className="metric-value-minimal">{consensusMetrics?.current_epoch || 'N/A'}</div>
            <div className="metric-unit-minimal">epoch</div>
          </div>
        </div>
        
        <div className="metric-card-minimal">
          <div className="metric-content-minimal">
            <div className="metric-label-minimal">Total Transactions</div>
            <div className="metric-value-minimal">{displayTotalTxs}</div>
          </div>
        </div>
        
        <div className="metric-card-minimal">
          <div className="metric-content-minimal">
            <div className="metric-label-minimal">Real TPS</div>
            <div className="metric-value-minimal">{performanceMetrics?.real_tps?.toLocaleString() || tps}</div>
            <div className="metric-unit-minimal">tx/s</div>
          </div>
        </div>
        
        <div className="metric-card-minimal">
          <div className="metric-content-minimal">
            <div className="metric-label-minimal">Total Supply</div>
            <div className="metric-value-minimal" title={`${(supply?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })} AVO exact`}>
              {formatSupplyValue(supply?.total || 0)}
            </div>
            <div className="metric-unit-minimal">AVO</div>
          </div>
        </div>
      </div>

      {/* Main Activity - Only 2 Columns */}
      <div className="activity-container-minimal">
        <div className="activity-column">
          <div className="activity-section-minimal">
            <div className="activity-header">
              <h3 className="activity-title-minimal">Latest Blocks</h3>
              <div className="view-controls">
                {!showAllBlocks ? (
                  <button 
                    className="view-all-btn"
                    onClick={() => setShowAllBlocks(true)}
                    disabled={recentBlocks.length <= itemsPerPage}
                  >
                    View All ({recentBlocks.length})
                  </button>
                ) : (
                  <button 
                    className="view-latest-btn"
                    onClick={() => {
                      setShowAllBlocks(false);
                      setBlocksPage(1);
                    }}
                  >
                    Latest Only
                  </button>
                )}
              </div>
            </div>
            <div className="blocks-list-minimal">
              {recentBlocks.length > 0 ? (
                getDisplayedBlocks().map((block, index) => (
                  <div key={block.hash || index} className="block-item-minimal">
                    <div className="block-header">
                      <span className="block-number">#{block.number}</span>
                      <span className="block-time">{formatTimestamp(block.timestamp, 'time')}</span>
                    </div>
                    <div className="block-details">
                      <div 
                        className="block-hash clickable-hash" 
                        onClick={() => setSelectedBlockHash(block.hash)}
                        title="Click to view block details"
                      >
                        {block.hash?.slice(0, 16)}...{block.hash?.slice(-8)}
                      </div>
                      <div className="block-txs">{block.transactionCount} transactions</div>
                      <div className="block-validator">Validator: {block.validator}</div>
                      <div className="block-gas">Gas: {parseInt(block.gasUsed).toLocaleString()}/{parseInt(block.gasLimit).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-blocks">
                  <p>No recent blocks found</p>
                  <p style={{fontSize: '12px', color: '#252525ff'}}>
                    Make a transfer using the CLI to generate blocks
                  </p>
                </div>
              )}
            </div>
            {showAllBlocks && getTotalBlocksPages() > 1 && (
              <div className="pagination-controls">
                <div className="pagination-numbers">
                  {generatePageNumbers(blocksPage, getTotalBlocksPages()).map((page, index) => (
                    <button
                      key={index}
                      className={`pagination-number ${page === blocksPage ? 'active' : ''} ${page === '...' ? 'ellipsis' : ''}`}
                      onClick={() => page !== '...' && setBlocksPage(page)}
                      disabled={page === '...' || page === blocksPage}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <div className="pagination-info">
                  Showing {Math.min(itemsPerPage, recentBlocks.length - (blocksPage - 1) * itemsPerPage)} of {recentBlocks.length} blocks
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="activity-column">
          <div className="activity-section-minimal">
            <div className="activity-header">
              <h3 className="activity-title-minimal">Latest Transactions</h3>
              <div className="view-controls">
                {!showAllTransactions ? (
                  <button 
                    className="view-all-btn"
                    onClick={() => setShowAllTransactions(true)}
                    disabled={recentTransactions.length <= itemsPerPage}
                  >
                    View All ({recentTransactions.length})
                  </button>
                ) : (
                  <button 
                    className="view-latest-btn"
                    onClick={() => {
                      setShowAllTransactions(false);
                      setTransactionsPage(1);
                    }}
                  >
                    Latest Only
                  </button>
                )}
              </div>
            </div>
            <div className="transactions-list-minimal">
              {recentTransactions.length > 0 ? (
                getDisplayedTransactions().map((tx, index) => (
                  <div key={tx.hash || index} className="transaction-item-minimal">
                    <div className="tx-header">
                      <span 
                        className="tx-hash clickable-hash" 
                        onClick={() => setSelectedTransactionHash(tx.hash)}
                        title="Click to view transaction details"
                      >
                        {tx.hash?.slice(0, 16)}...{tx.hash?.slice(-8) || 'N/A'}
                      </span>
                      <span className="tx-time">{formatTimestamp(tx.timestamp, 'time')}</span>
                    </div>
                    <div className="tx-details">
                      <div className="tx-amount">{parseFloat(tx.valueInAVO || 0).toFixed(4)} AVO</div>
                    </div>
                    <div className="tx-extra">
                      <div className="tx-from">From: {tx.from?.slice(0, 8)}...{tx.from?.slice(-6)}</div>
                      <div className="tx-to">To: {tx.to?.slice(0, 8)}...{tx.to?.slice(-6)}</div>
                      <div className="tx-fee">Fee: {parseFloat(tx.gasFeeInAVO || 0).toFixed(6)} AVO</div>
                      <div className="tx-status" style={{color: tx.status === 'success' ? '#4CAF50' : '#f44336'}}>
                        {tx.status}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-transactions">
                  <p>No recent transactions found</p>
                  <p style={{fontSize: '12px', color: '#272727ff'}}>
                    Make a transfer using the CLI to see transactions here
                  </p>
                </div>
              )}
            </div>
            {showAllTransactions && getTotalTransactionsPages() > 1 && (
              <div className="pagination-controls">
                <div className="pagination-numbers">
                  {generatePageNumbers(transactionsPage, getTotalTransactionsPages()).map((page, index) => (
                    <button
                      key={index}
                      className={`pagination-number ${page === transactionsPage ? 'active' : ''} ${page === '...' ? 'ellipsis' : ''}`}
                      onClick={() => page !== '...' && setTransactionsPage(page)}
                      disabled={page === '...' || page === transactionsPage}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <div className="pagination-info">
                  Showing {Math.min(itemsPerPage, recentTransactions.length - (transactionsPage - 1) * itemsPerPage)} of {recentTransactions.length} transactions
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      
      {/* Network Metrics Section - Real Blockchain Data */}
      <NetworkMetrics 
        rpcUrl="http://localhost:9545"
        networkStats={networkStats || {}}
      />

      {/* Transaction Details Modal */}
      {selectedTransactionHash && (
        <TransactionDetails 
          transactionHash={selectedTransactionHash}
          onClose={() => setSelectedTransactionHash(null)}
        />
      )}
      
      {/* Block Details Modal */}
      {selectedBlockHash && (
        <BlockDetails 
          blockHash={selectedBlockHash}
          onClose={() => setSelectedBlockHash(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;